import {
	$applyNodeReplacement,
	DecoratorNode,
	type NodeKey,
	type SerializedLexicalNode
} from 'lexical';
import type React from 'react';

export interface SerializedMentionNode extends SerializedLexicalNode {
	value: string;
	id: string;
}

export class MentionNode extends DecoratorNode<React.JSX.Element> {
	__value: string;
	__id: string;

	static getType(): string {
		return 'mention';
	}

	static clone(node: MentionNode): MentionNode {
		return new MentionNode(node.__value, node.__id, node.__key);
	}

	constructor(value: string, id: string, key?: NodeKey) {
		super(key);
		this.__value = value;
		this.__id = id;
	}

	createDOM(): HTMLElement {
		const element = document.createElement('span');
		element.className = 'mention-node';
		element.setAttribute('data-mention-value', this.__value);
		element.setAttribute('data-mention-id', this.__id);
		element.setAttribute('contenteditable', 'false');
		return element;
	}

	updateDOM(): false {
		return false;
	}

	getValue(): string {
		return this.__value;
	}

	getId(): string {
		return this.__id;
	}

	setValue(value: string): void {
		const writable = this.getWritable();
		writable.__value = value;
	}

	setId(id: string): void {
		const writable = this.getWritable();
		writable.__id = id;
	}

	decorate(): React.JSX.Element {
		return (
			<span
				className="mention-node"
				contentEditable={false}
				data-mention-id={this.__id}
				data-mention-value={this.__value}
			>
				{`@{${this.__value}}`}
			</span>
		);
	}

	static importJSON(serializedNode: SerializedMentionNode): MentionNode {
		const { value, id } = serializedNode;
		const node = $createMentionNode(value, id);
		return node;
	}

	exportJSON(): SerializedMentionNode {
		return {
			value: this.__value,
			id: this.__id,
			type: 'mention',
			version: 1
		};
	}

	isKeyboardSelectable(): boolean {
		return true;
	}

	isInline(): boolean {
		return true;
	}
}

export function $createMentionNode(value: string, id: string): MentionNode {
	const mentionNode = new MentionNode(value, id);
	return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(node: unknown): node is MentionNode {
	return node instanceof MentionNode;
}
